/**
 * Accordion Validation Utilities
 * Validation functions for each section in the edit profile page
 * Returns validation status and summary text for completed sections
 */

import type { PrivacySettings } from "@/features/profile/types/profile.types"

export interface SectionValidationResult {
  isComplete: boolean
  summary?: string
  displayName?: string
  error?: string
}

export interface ProfileFormValues {
  name?: string
  username?: string
  bio?: string
  website?: string
}

/**
 * Validates the profile info section (name, username, city, description)
 */
export const validateProfileInfoSection = (
  values: ProfileFormValues,
  city?: string,
): SectionValidationResult => {
  const errors: string[] = []

  // Name validation - required, min 2 chars
  if (!values.name || values.name.length < 2) {
    errors.push("Name is required")
  }

  // Username validation - required, min 3 chars
  if (!values.username || values.username.length < 3) {
    errors.push("Username is required")
  }

  // City validation - required
  if (!city || city.trim().length === 0) {
    errors.push("City is required")
  }

  // Description validation - required
  if (!values.bio || values.bio.trim().length === 0) {
    errors.push("Description is required")
  }

  const isComplete = errors.length === 0

  // Generate summary when complete - show name and username
  const summary = isComplete ? `${values.name} @${values.username}`.trim() : undefined

  return { isComplete, summary }
}

/**
 * Validates the photos section - requires at least 1 photo (cover or gallery)
 */
export const validatePhotosSection = (
  coverCount: number,
  galleryCount: number,
): SectionValidationResult => {
  const errors: string[] = []
  const totalCount = coverCount + galleryCount

  if (totalCount === 0) {
    errors.push("At least one photo is required")
  }

  const isComplete = errors.length === 0
  const summary = isComplete ? `${totalCount} image${totalCount !== 1 ? "s" : ""}` : undefined

  return { isComplete, summary }
}

/**
 * Validates the spiritual traditions section - requires traditions OR backgrounds (multi-select)
 * Also considers "Others" text input fields (now arrays)
 */
export const validateTraditionsSection = (
  selectedTraditions: Array<{ id: string; title: string; is_custom?: boolean }>,
  selectedBackgrounds: Array<{ id: string; title: string; is_custom?: boolean }>,
  otherTraditions: string[],
  otherBackgrounds: string[],
): SectionValidationResult => {
  const errors: string[] = []

  // Check if user has selected from list OR entered custom text
  const hasTradition =
    selectedTraditions.length > 0 || otherTraditions.some(t => t && t.trim().length > 0)
  const hasBackground =
    selectedBackgrounds.length > 0 || otherBackgrounds.some(b => b && b.trim().length > 0)

  if (!hasTradition && !hasBackground) {
    errors.push("At least one spiritual tradition or ethnic background is required")
  }

  const isComplete = errors.length === 0

  // Note: We can't get the names here since we only have IDs
  // The summary will be generated in the component using the actual data
  const summary = isComplete ? "Spiritual profile configured" : undefined

  return { isComplete, summary }
}

/**
 * Generates summary for traditions with names
 * Also includes custom "Others" values if provided (now arrays)
 */
export const getTraditionsSummary = (
  selectedTraditions: Array<{ id: string; title: string; is_custom?: boolean }>,
  selectedBackgrounds: Array<{ id: string; title: string; is_custom?: boolean }>,
  _traditionsData: Array<{ id: string; title: string }>,
  _backgroundsData: Array<{ id: string; title: string }>,
  otherTraditions: string[],
  otherBackgrounds: string[],
): string => {
  const traditionNames = selectedTraditions.map(t => t.title).filter(Boolean)

  const backgroundNames = selectedBackgrounds.map(b => b.title).filter(Boolean)

  // Add custom "Others" values if provided
  const customTraditions = otherTraditions.filter(t => t && t.trim().length > 0).map(t => t.trim())
  const customBackgrounds = otherBackgrounds
    .filter(b => b && b.trim().length > 0)
    .map(b => b.trim())

  const allNames = [
    ...traditionNames,
    ...customTraditions,
    ...backgroundNames,
    ...customBackgrounds,
  ]

  if (allNames.length === 0) {
    return ""
  }

  // Show first 2 items, then "+N more" if there are more
  if (allNames.length <= 2) {
    return allNames.join(", ")
  }

  return `${allNames.slice(0, 2).join(", ")} +${allNames.length - 2} more`
}

/**
 * Validates the intentions section - requires at least 1 intention
 */
export const validateIntentionsSection = (
  selectedIntentions: string[],
): SectionValidationResult => {
  const errors: string[] = []

  if (selectedIntentions.length === 0) {
    errors.push("At least one intention is required")
  }

  const isComplete = errors.length === 0
  const summary = isComplete
    ? `${selectedIntentions.length} intention${selectedIntentions.length !== 1 ? "s" : ""} selected`
    : undefined

  return { isComplete, summary }
}

/**
 * Generates summary for intentions with names
 */
export const getIntentionsSummary = (
  intentionIds: string[],
  intentionsData: Array<{ id: string; title: string }>,
): string => {
  const intentionNames = intentionIds
    .map(id => intentionsData.find(i => i.id === id)?.title)
    .filter(Boolean)

  if (intentionNames.length === 0) {
    return ""
  }

  // Show first 3 items, then "+N more" if there are more
  if (intentionNames.length <= 3) {
    return intentionNames.join(", ")
  }

  return `${intentionNames.slice(0, 3).join(", ")} +${intentionNames.length - 3} more`
}

/**
 * Validates the privacy section - requires any privacy settings configured
 */
export const validatePrivacySection = (
  _privacySettings: PrivacySettings,
): SectionValidationResult => {
  // Privacy is always "complete" since defaults exist
  const isComplete = true
  const summary = "Privacy settings configured"

  return { isComplete, summary }
}
